import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useGateStateStream } from '../hooks/useGateStateStream';
import type { GateSlot, GateWaiter } from '../types/systemHealth.types';

function formatSecs(ms: number): string {
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.floor(s % 60)}s`;
}

/** Origin label: background workers get a muted `·bg` suffix per the design. */
function originLabel(origin: string, interactive: boolean): string {
  if (!interactive || origin === 'background') return `${origin === 'background' ? 'worker' : origin} ·bg`;
  return origin;
}

/**
 * Live NetSuite concurrency-gate panel (86d3peur0 Phase C) — the SSE surface. Renders the fixed permit slots
 * + the scrolling wait queue + gate stats from `GateStateSnapshot` pushes. Elapsed counters tick locally: the
 * server→client clock offset is captured once per snapshot, then `serverNow = Date.now() + offset` advances
 * smoothly between pushes. Reflow-proof: permit tiles are a fixed 84px and the queue a fixed 96px scroll lane.
 */
export function GatePanel() {
  const { snapshot, connectionState } = useGateStateStream();

  // Anchor the server clock once per snapshot, then tick locally every second.
  const offsetRef = useRef(0);
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (snapshot) offsetRef.current = Date.parse(snapshot.serverNowUtc) - Date.now();
  }, [snapshot]);
  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const permitLimit = snapshot?.permitLimit ?? 2;
  const serverNow = Date.now() + offsetRef.current;
  const elapsed = (iso: string) => formatSecs(Math.max(0, serverNow - Date.parse(iso)));

  const slots: (GateSlot | null)[] = Array.from(
    { length: permitLimit },
    (_, i) => snapshot?.inFlight[i] ?? null,
  );
  const queue: GateWaiter[] = snapshot?.waiting ?? [];
  const queueOverLimit = queue.length > permitLimit;
  const peakOverLimit = (snapshot?.peakInFlight ?? 0) > permitLimit;

  const dotTone =
    connectionState === 'open' ? 'bg-success' : connectionState === 'reconnecting' ? 'bg-warning' : 'bg-muted-foreground';
  const connText =
    connectionState === 'open' ? 'Live · SSE stream' : connectionState === 'reconnecting' ? 'Reconnecting…' : 'Connecting…';

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
        <div className="min-w-0 space-y-1">
          <CardTitle className="text-base">Concurrency gate — live</CardTitle>
          <p className="text-xs text-muted-foreground">
            Only {permitLimit} NetSuite calls may run at once (N={permitLimit}, the shared-account limit) ·
            interactive first
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
          <span className={cn('h-1.5 w-1.5 rounded-full', dotTone, connectionState === 'open' && 'animate-pulse')} />
          {connText}
        </span>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 lg:grid-cols-2 items-start">
          {/* Left: permits + queue */}
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Permits in use</span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {snapshot?.inFlightCount ?? 0} / {permitLimit} in flight
                </span>
              </div>
              <div className="grid gap-2 grid-cols-2">
                {slots.map((slot, i) => (
                  <div
                    key={i}
                    className={cn(
                      'flex h-[84px] flex-col justify-center rounded-lg border p-3',
                      slot ? 'border-info/40 bg-info/5' : 'border-dashed bg-muted/30',
                    )}
                  >
                    {slot ? (
                      <>
                        <span
                          className={cn(
                            'truncate text-sm font-medium',
                            slot.interactive ? 'text-info' : 'text-muted-foreground',
                          )}
                          title={originLabel(slot.origin, slot.interactive)}
                        >
                          {originLabel(slot.origin, slot.interactive)}
                        </span>
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {slot.interactive ? 'interactive' : 'background'} · {elapsed(slot.acquiredAtUtc)}
                        </span>
                      </>
                    ) : (
                      <span className="text-center text-sm text-muted-foreground">Available</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Waiting queue</span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs tabular-nums',
                    queueOverLimit ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {queue.length} waiting
                </span>
              </div>
              <div className="h-24 space-y-1 overflow-y-auto rounded-lg border bg-muted/20 p-2">
                {queue.length ? (
                  queue.map((w, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="tabular-nums text-muted-foreground">{i + 1}.</span>
                        <span
                          className={cn('truncate', w.interactive ? 'text-info' : 'text-muted-foreground')}
                          title={originLabel(w.origin, w.interactive)}
                        >
                          {originLabel(w.origin, w.interactive)}
                        </span>
                      </span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">{elapsed(w.enqueuedAtUtc)}</span>
                    </div>
                  ))
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                    No calls waiting
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: stats */}
          <div className="grid grid-cols-2 gap-3">
            <GateStat
              label="Peak in flight"
              value={snapshot?.peakInFlight ?? 0}
              valueClassName={peakOverLimit ? 'text-warning' : undefined}
              hint="High-water mark since the app started"
            />
            <GateStat label="In queue now" value={snapshot?.waitingCount ?? 0} hint="Calls currently waiting for a permit" />
            <GateStat
              label="Avg wait"
              value={`${Math.round(snapshot?.avgWaitMs ?? 0)} ms`}
              hint="Mean gate wait over all acquired permits since start"
            />
            <GateStat
              label="Gate timeouts"
              value={snapshot?.totalTimeouts ?? 0}
              valueClassName={(snapshot?.totalTimeouts ?? 0) > 0 ? 'text-destructive' : undefined}
              hint="Calls that gave up waiting for a permit (since start)"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GateStat({
  label,
  value,
  valueClassName,
  hint,
}: {
  label: string;
  value: string | number;
  valueClassName?: string;
  hint: string;
}) {
  return (
    <div className="rounded-lg border p-3" title={hint}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn('mt-1 text-xl font-bold tabular-nums', valueClassName)}>{value}</p>
    </div>
  );
}
